import React from 'react';
import PropTypes from 'prop-types';

/**
 * Five-day forecast strip. Days where the forecast suggests skipping a watering
 * cycle are flagged, since that is the decision this widget exists to support.
 */

const CONDITION_ICONS = {
  sunny: 'ri-sun-line',
  cloudy: 'ri-cloudy-line',
  rain: 'ri-showers-line',
  storms: 'ri-thunderstorms-line',
};

const CONDITION_LABELS = {
  sunny: 'Sunny',
  cloudy: 'Cloudy',
  rain: 'Rain',
  storms: 'Storms',
};

const formatDay = (isoDate, index) => {
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  if (index === 0) return 'Today';
  return date.toLocaleDateString(undefined, { weekday: 'short' });
};

export default function ForecastWidget({ forecast, isLoading }) {
  if (isLoading) {
    return <p className="lawn-widget-muted">Loading forecast…</p>;
  }

  if (!forecast || forecast.length === 0) {
    return (
      <p className="lawn-widget-muted">
        No forecast available for this property yet.
      </p>
    );
  }

  const skipDays = forecast.filter((day) => day.rainSkipRecommended).length;

  return (
    <div className="lawn-forecast">
      <p className="lawn-forecast-summary">
        {skipDays > 0
          ? `${skipDays} of the next ${forecast.length} days suggest skipping a cycle.`
          : 'No rain skips recommended this week.'}
      </p>

      <ul className="lawn-forecast-strip">
        {forecast.map((day, index) => (
          <li
            key={day.id}
            className={`lawn-forecast-day${
              day.rainSkipRecommended ? ' lawn-forecast-day-skip' : ''
            }`}
          >
            <span className="lawn-forecast-weekday">{formatDay(day.date, index)}</span>
            <i
              className={`${CONDITION_ICONS[day.condition] || 'ri-cloud-line'} lawn-forecast-icon`}
              title={CONDITION_LABELS[day.condition] || day.condition}
              aria-hidden="true"
            ></i>
            <span className="lawn-forecast-temps">
              <strong>{day.highF}°</strong> / {day.lowF}°
            </span>
            <span className="lawn-forecast-precip">
              <i className="ri-drop-line" aria-hidden="true"></i> {day.precipChancePct}%
            </span>
            {day.rainSkipRecommended && (
              <span className="lawn-forecast-flag">Skip watering</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

ForecastWidget.propTypes = {
  forecast: PropTypes.array,
  isLoading: PropTypes.bool,
};

ForecastWidget.defaultProps = {
  forecast: [],
  isLoading: false,
};
